import {AfterViewInit, Component, Input, OnInit, TemplateRef, Type, ViewChild, ViewContainerRef} from '@angular/core';
import {View} from "src/app/common/view";
import {Handler} from "src/app/common/handler";
import {HandleQuery} from "../../common/handle-query";
import {ChartOptions, ChartOptionType} from "../dashboard/dashboard.types";
import {combineLatest, map, mergeMap, Observable, of, pairwise, ReplaySubject, skipWhile, Subject, take} from "rxjs";
import lodash, {cloneDeep} from "lodash";
import {DashboardContext} from "../dashboard/dashboard.context";
import {AppContext, CompletenessInfo, IncidentsResult} from "../../app-context";
import {
    AggregatedDataPoint,
    Connection,
    ContractMeasurementResult,
    DataType,
    DayOfWeekMeasurementsResult,
    GetContractMeasurements,
    GetDayOfWeekMeasurements,
    GetLocationMeasurements,
    GetMeasurements,
    GetPortfolioMeasurements,
    Location,
    LocationMeasurementsResult,
    MeasurementsResult,
    MeterType,
    Organisation,
    PortfolioMeasurementsResult,
    SourceIds,
    TimeRange,
    TimeResolution
} from "@flowmaps/flowmaps-typescriptmodels";
import rfdc from 'rfdc';
import {openModal, sendQuery} from "../../common/app-common-utils";
import moment from "moment";
import {
    asSourceIds,
    entityToSourceInfo,
    SourceInfo,
    SourceSelectionChangeEvent
} from "../../utils/source-providers/sources-provider";
import {EntityType} from "../../handlers/entity";
import {HandleEvent} from "../../common/handle-event";
import {ChartDataPerMeasurement, DashboardTime, TimeChangedEvent} from "../../utils/chart-data-provider";
import {ChartModalOptions} from "../charts/chart-utils.service";
import {HandleCommand} from "../../common/handle-command";
import {AnimationTypes} from "../../common/modal/modal";
import {MeterViewEntity} from "../../handlers/meter-views-standalone-handler";
import {filter} from 'rxjs/operators';

const clone = rfdc();

@Component({
    selector: 'app-measurements-handler',
    templateUrl: './measurements-handler.component.html',
    styleUrls: ['./measurements-handler.component.scss']
})
@Handler()
export class MeasurementsHandlerComponent extends View implements OnInit, AfterViewInit {
    data: MeasurementsHandlerComponentData;
    @Input() locationDashboard: boolean;
    @Input() useWrapperForChartOptions: boolean;
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    dashboardHistory: DashboardTime[] = [];

    filtersSubject: Subject<DataQueryFilters>;
    isPortfolioSubject: Subject<boolean>;
    chartOptionsSubject: Subject<{ [P in ChartOptionType]?: ChartOptions }>;
    lastOptionsValue: { [P in ChartOptionType]?: ChartOptions };
    rendered: boolean;
    rendering: boolean;

    constructor() {
        super();
        this.filtersSubject = new ReplaySubject(1);
        this.chartOptionsSubject = new ReplaySubject(1);
        this.publishChartOptionsChange();
        this.isPortfolioSubject = new ReplaySubject(1);
        this.isPortfolioSubject.pipe(pairwise())
            .subscribe(([previousValue, currentValue]) => {
                if (previousValue !== currentValue) {
                    this.publishEvent("dashboardTypeChanged", currentValue);
                }
            });
    }

    ngOnInit() {
        if (lodash.has(this.data, "allSelectedSources")) {
            this.updateIsLocationDashboard();
        }
        this.isPortfolioSubject.next(!this.locationDashboard);
    }

    ngAfterViewInit() {
        this.rendering = true;
        this.setData = this.data;
        this.rendered = true;
    }

    private updateIsLocationDashboard() {
        this.locationDashboard = this.data.selectedSources?.length === 1 && this.data.selectedSources[0]?.type === EntityType.location;
    }

    @Input("data")
    set setData(data: MeasurementsHandlerComponentData) {
        const backupFilters: DataQueryFilters = {
            timeRange: this.data?.timeRange,
            allSelectedSources: this.data?.allSelectedSources,
            sources: this.data?.selectedSources,
            cleanedSources: this.cleanupSelection(this.data?.selectedSources),
            fullScreen: data?.chartModalData?.fullScreen
        };
        this.data = data;
        if (this.rendering) {
            if (data.component) {
                this.renderSubComponent(data.component);
            }
        }
        this.publishFiltersChanged({
            timeRange: this.dashboardTimeToQuery(this.data.timeRange),
            sources: this.data.selectedSources,
            cleanedSources: this.cleanupSelection(this.data.selectedSources),
            allSelectedSources: this.data.allSelectedSources,
            fullScreen: this.data.chartModalData?.fullScreen
        }, backupFilters);
        this.publishChartOptionsChange();
    }

    private publishChartOptionsChange() {
        const newValue = this.data?.chartOptions || DashboardContext.defaultDashboard().info.chartOptions;
        if (!lodash.isEqual(newValue, this.lastOptionsValue)) {
            this.lastOptionsValue = newValue;
            this.chartOptionsSubject.next(newValue);
        }
    }

    private publishFiltersChanged(filters: DataQueryFilters, currentFilters?: DataQueryFilters) {
        const sourcesChanged = !lodash.isEqual(lodash.omit(filters, "allSelectedSources"), lodash.omit(currentFilters, "allSelectedSources"));
        if (sourcesChanged) {
            this.updateIsLocationDashboard();
            this.isPortfolioSubject.next(!this.locationDashboard);
            this.publishEvent("filtersChanged", filters);
        }
        if (sourcesChanged || !this.rendered || this.data.chartModalData?.fullScreen) {
            this.filtersSubject.next(filters);
        }
    }

    @HandleEvent("sourceSelectionChange")
    sourceSelectionChange(selectedSources: SourceSelectionChangeEvent, event: CustomEvent) {
        this.data.selectedSources = selectedSources.selectedItems;
        this.data.allSelectedSources = selectedSources.allSelectedItems;
        this.filtersSubject.pipe(take(1)).subscribe(f => this.publishFiltersChanged((<DataQueryFilters>{
            ...f,
            sources: this.data.selectedSources,
            cleanedSources: this.cleanupSelection(this.data.selectedSources),
            allSelectedSources: this.data.allSelectedSources
        }), f));
        if (this.data.chartModalData?.fullScreen) {
            event.stopPropagation();
            return false;
        }
    }

    @HandleEvent("timeChanged")
    timeChanged(payload: TimeChangedEvent, event: CustomEvent) {
        if (payload.historyType === "add") {
            this.dashboardHistory.push(this.data.timeRange);
        }
        if (payload.historyType === "remove") {
            this.dashboardHistory.pop();
        }
        this.data.timeRange = payload.timeRange;
        this.filtersSubject.pipe(take(1)).subscribe(f => this.publishFiltersChanged((<DataQueryFilters>{
            ...f,
            timeRange: this.dashboardTimeToQuery(this.data.timeRange)
        }), f));
        if (this.data.chartModalData?.fullScreen) {
            event.stopPropagation();
            return false;
        }
    }

    @HandleQuery("getDashboardHistory")
    getDashboardHistory(): Observable<DashboardTime[]> {
        return of(this.dashboardHistory);
    }

    @HandleQuery("isPortfolio")
    isPortfolio(): Observable<boolean> {
        return this.isPortfolioSubject;
    }

    @HandleQuery("groupByEntity")
    groupByEntity(): Observable<boolean> {
        return this.subscribeTo("getChartDataQueryFilters")
            .pipe(skipWhile((d: DataQueryFilters) => !d.sources))
            .pipe(map((d: DataQueryFilters) => this.locationDashboard || (!!this.data.selectedSources.length
                && this.data.selectedSources.every(s => s.type === EntityType.meter)) ||
                this.data.selectedSources.length == 1 && this.data.selectedSources[0].type === EntityType.connection &&
                this.data.selectedSources[0].source.connection.meters.some(m => m.info.type === MeterType.INTERMEDIATE)));
    }

    @HandleQuery("onlyMetersSelected")
    onlyMetersSelected(): Observable<boolean> {
        return this.subscribeTo("getChartDataQueryFilters")
            .pipe(skipWhile((d: DataQueryFilters) => !d.sources))
            .pipe(map((d: DataQueryFilters) => !!this.data.selectedSources.length
                && this.data.selectedSources.every(s => s.type === EntityType.meter)));
    }

    @HandleQuery("getChartOptions")
    getChartOptions(type: ChartOptionType): Observable<ChartOptions> {
        if (this.useWrapperForChartOptions) {
            return this.sendQuery("getWrappedChartOptions", type);
        }
        if (lodash.isEmpty(type)) {
            console.warn("Dashboard chart option type is required to get chart options");
            return of(null);
        }
        return this.chartOptionsSubject
            .pipe(map(c => c[type] || DashboardContext.defaultDashboard().info.chartOptions[type]));
    }

    @HandleQuery("getChartDataQueryFilters")
    getChartDataQueryFilters(): Subject<DataQueryFilters> {
        return this.filtersSubject;
    }

    @HandleQuery("getTimeRange")
    getTimeRange(): Observable<DashboardTime> {
        return this.subscribeTo("getChartDataQueryFilters").pipe(map((f: DataQueryFilters) => f.timeRange));
    }

    @HandleQuery("getContractMeasurements")
    getContractMeasurements(): Observable<ContractMeasurementResult> {
        return this.subscribeTo("getChartDataQueryFilters").pipe(mergeMap((filters: DataQueryFilters) => {
            return filters.timeRange && filters.sources ? sendQuery("com.flowmaps.api.measurements.GetContractMeasurements", <GetContractMeasurements>{
                timeRange: filters.timeRange,
                sourceIds: asSourceIds(this.cleanupSelection(this.data.selectedSources)),
                resolution: filters.timeRange.resolution,
                unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution)
            }) : of(<ContractMeasurementResult>{
                co2measurements: {
                    measurements: {},
                    estimatedMeasurements: {}
                },
                costsMeasurements: []
            });
        }));
    }

    @HandleQuery("getDayOfWeekMeasurements")
    getDayOfWeekMeasurements(): Observable<DayOfWeekMeasurementsResult> {
        return this.subscribeTo("getChartDataQueryFilters").pipe(mergeMap((filters: DataQueryFilters) =>
            filters.timeRange && filters.sources && moment(filters.timeRange.end).diff(moment(filters.timeRange.start), "day") < 367
                ? sendQuery("com.flowmaps.api.measurements.GetDayOfWeekMeasurements", <GetDayOfWeekMeasurements>{
                    timeRange: filters.timeRange,
                    sources: asSourceIds(this.cleanupSelection(this.data.selectedSources))
                }) : of({measurements: {}})));
    }

    @HandleQuery("getCompleteness")
    getCompleteness(timeRange: TimeRange): Observable<CompletenessInfo[]> {
        return this.getChartDataQueryFilters().pipe(mergeMap(result => {
            if (result.sources) {
                const emptyConnections = result.allSelectedSources.filter(c => c.type === EntityType.connection)
                    .map(s => s.source.connection as Connection)
                    .filter(s => !s.meters.length);
                return this.sendQuery("getPrimaryMeterViews", asSourceIds(result.sources))
                    .pipe(map((m: MeterViewEntity[]) => AppContext.computeCompletenessPerTimeRange(m, timeRange, emptyConnections)));
            }
            return of([]);
        }))
    }

    @HandleQuery("getIncidents")
    getIncidents(): Observable<IncidentsResult[]> {
        return this.getChartDataQueryFilters().pipe(mergeMap(result => {
                if (!result.sources) return of([]);
                return this.sendQuery("getMeterViews", asSourceIds(result.sources)).pipe(
                    map((meterViews: MeterViewEntity[]) =>  AppContext.computeIncidents(meterViews)));
            }));
    }

    @HandleQuery("getMeasurementsData")
    getMeasurementsData(payload: MeasurementsQueryPayload): Observable<ChartDataPerMeasurement> {
        const { range, showIncidents = false } = payload;
        const timeRange = lodash.isEmpty(range) ? this.data.timeRange : range;
        const base = this.subscribeTo("getChartDataQueryFilters")
            .pipe(mergeMap(filters => {
                    const dashboard = DashboardContext.dashboardFromUrl(window.location.pathname);
                    const onlyLocationsSelected = (dashboard?.info.sources?.locationIds?.length ?? 0) > 0 &&
                        Object.entries(dashboard?.info.sources ?? {}).filter(([key]) => key !== "locationIds").every(([, arr]) => (arr?.length ?? 0) === 0);
                    const locationDashboard = !this.rendered ? onlyLocationsSelected : this.locationDashboard;
                    if (locationDashboard) {
                        return this.getLocationMeasurements({...filters, timeRange})
                            .pipe(map(data => !lodash.isEmpty(data)
                                ? <ChartDataPerMeasurement>{
                                    totals: data.byMeter,
                                    totalsPerYear: data.totalsPerYear,
                                    byEntity: data.byMeter
                                } : {}));
                    }
                    return this.getPortfolioMeasurements({...filters, timeRange},!this.rendered ? dashboard.info.sources : null)
                        .pipe(map(data => !lodash.isEmpty(data)
                        ? <ChartDataPerMeasurement>{
                            totals: [data.totals],
                            byEntity: data.byEntity,
                            entityType: data.entityType,
                        } : {}));
            }));
        const incidents = showIncidents ? this.getIncidents() : of([]);
        return combineLatest([base, incidents]).pipe(
            map(([base, incidents]) => ({ ...base, incidents }))
        );
    }

    @HandleQuery("getTotals")
    getTotals(payload: MeasurementsQueryPayload): Observable<ChartDataPerMeasurement> {
        const { range, showIncidents = false } = payload;
        const timeRange = lodash.isEmpty(range) ? this.data.timeRange : range;
        const base = this.subscribeTo("getChartDataQueryFilters").pipe(
            filter(filters => !!filters),
            mergeMap(filters => this.getMeasurements({...filters, timeRange})),
            map(data => {
                if (lodash.isEmpty(data)) return {};
                return {
                    totals: [data],
                    byEntity: []
                } as ChartDataPerMeasurement;
            })
        );
        const incidents = showIncidents ? this.getIncidents() : of([]);
        return combineLatest([base, incidents]).pipe(
            map(([base, incidents]) => ({ ...base, incidents }))
        );
    }

    @HandleQuery("getMeasurements")
    getMeasurements(queryFilters?: DataQueryFilters): Observable<PortfolioMeasurementsResult> {
        return (lodash.isEmpty(queryFilters) ? this.sendQuery("getChartDataQueryFilters") : of(queryFilters)).pipe(mergeMap((filters: DataQueryFilters) =>
            filters.timeRange
                ? sendQuery("com.flowmaps.api.measurements.GetMeasurements", <GetMeasurements>{
                    timeRange: {start: filters.timeRange.start, end: filters.timeRange.end},
                    sources: asSourceIds(this.cleanupSelection(this.data.selectedSources)),
                    resolution: filters.timeRange.resolution,
                    unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution),
                    addWeather: true,
                    addPower: false
                })
                : of({})));
    }

    @HandleQuery("getPortfolioMeasurements")
    getPortfolioMeasurements(queryFilters?: DataQueryFilters, sources?: SourceIds): Observable<PortfolioMeasurementsResult> {
        return (lodash.isEmpty(queryFilters) ? this.sendQuery("getChartDataQueryFilters") : of(queryFilters)).pipe(mergeMap((filters: DataQueryFilters) =>
            filters.timeRange
                ? sendQuery("com.flowmaps.api.measurements.GetPortfolioMeasurements", <GetPortfolioMeasurements>{
                    timeRange: {start: filters.timeRange.start, end: filters.timeRange.end},
                    sources: sources || asSourceIds(this.cleanupSelection(this.data.selectedSources)),
                    resolution: filters.timeRange.resolution,
                    unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution)
                })
                : of({})));
    }

    @HandleQuery("getLocationMeasurements")
    getLocationMeasurements(queryFilters?: DataQueryFilters): Observable<LocationMeasurementsResult> {
        return (lodash.isEmpty(queryFilters) ? this.sendQuery("getChartDataQueryFilters") : of(queryFilters)).pipe(mergeMap((filters: DataQueryFilters) => {
            return filters.timeRange && filters.sources?.length
                ? sendQuery("com.flowmaps.api.measurements.GetLocationMeasurements", <GetLocationMeasurements>{
                    timeRange: {start: filters.timeRange.start, end: filters.timeRange.end},
                    locationIds: asSourceIds(this.data.allSelectedSources).locationIds,
                    resolution: filters.timeRange.resolution,
                    unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution)
                }) : of({});
        }));
    }

    @HandleCommand("openChartInModal")
    openChartInModal(command: MeasurementsHandlerOpenModalCommand): void {
        this.sendQuery("getChartDataQueryFilters").subscribe((f: DataQueryFilters) => {
            const modalOptions = cloneDeep(command.chartModalData);
            modalOptions.fullScreen = true;
            const data = <MeasurementsHandlerComponentData>{
                chartOptions: command.chartOptions,
                component: command.component,
                chartModalData: modalOptions,
                timeRange: f.timeRange,
                selectedSources: f.sources,
                allSelectedSources: f.allSelectedSources,
            };
            openModal(MeasurementsHandlerComponent, data, command.container, {
                cssClass: "modal-dialog-centered modal-xl d-flex align-items-stretch",
                style: "height: 90vh; min-height: 90vh; width: 90vw; min-width: 90vw;",
                animation: AnimationTypes.zoomIn
            });
        });
    }

    private cleanupSelection = (selection: SourceInfo[]): SourceInfo[] => {
        if (!selection?.length) {
            return selection;
        }
        const selectedIds = selection.map(s => s.id);
        let toBeDeleted: string[] = [];
        for (const sourceInfo of selection) {
            const parent = sourceInfo.source.getParentAsEntity();
            if (parent) {
                if (selectedIds.includes(parent.getEntityId())) {
                    toBeDeleted.push(sourceInfo.id);
                }
                if (parent.getChildIds().length > 1 && parent.getChildIds().every(c => selectedIds.includes(c))) {
                    toBeDeleted = toBeDeleted.concat(parent.getChildIds());
                    selection.push(entityToSourceInfo(parent));
                }
            }
        }
        if (toBeDeleted.length) {
            const newSelection = lodash.uniqBy(selection.filter(s => !toBeDeleted.includes(s.id)), s => s.id);
            return this.cleanupSelection(newSelection);
        }
        // For meters with children linking to this meter add all the direct subMeters as well
        return selection.flatMap(s => [s].concat(s.type === EntityType.meter ? s.source.getChildEntities().map(s => entityToSourceInfo(s)) : []))
    }

    private renderSubComponent(component: Type<unknown>) {
        const comp = component;
        if (comp instanceof TemplateRef) {
            this.container.createEmbeddedView(comp);
        } else {
            const componentRef = this.container.createComponent(comp);
            const component = componentRef.instance;
            component['data'] = this.data.chartModalData;
        }
    }

    private dashboardTimeToQuery = (time: DashboardTime): DashboardTime => {
        if (!time) {
            return null;
        }
        const timeRange = AppContext.timeRangeToQuery(time);
        return {
            start: timeRange.start,
            end: timeRange.end,
            label: time.label,
            resolution: time.resolution
        };
    }

    static mergeAliasesWithLocation(result: PortfolioMeasurementsResult, organisations: Organisation[]): MeasurementsResult[] {
        if (result.entityType !== "location") {
            return result.byEntity;
        }
        const locationMap = new Map<string, Location>();
        organisations.forEach(org => {org.locations.forEach(loc => {
                locationMap.set(loc.locationId, loc);
                loc.aliasIds?.forEach(aliasId => locationMap.set(aliasId, loc));
            });
        });

        const dataMap = new Map<string, MeasurementsResult>();
        result.byEntity?.forEach(r => dataMap.set(r.entityId, clone(r)));

        const mergedResults: MeasurementsResult[] = [];
        dataMap.forEach((r, entityId) => {
            const location = locationMap.get(entityId);
            if (!location || location.locationId !== entityId) {
                return;
            }
            if (!location.aliasIds?.length) {
                mergedResults.push(r);
                return;
            }
            location.aliasIds.forEach(aliasId => {
                const aliasResult = dataMap.get(aliasId);
                if (aliasResult) {
                    r = this.mergeMeasurementsResult(r, aliasResult);
                }
            });
            mergedResults.push(r);
        });
        return mergedResults;
    }

    private static mergeMeasurementsResult(destination: MeasurementsResult, other: MeasurementsResult): MeasurementsResult {
        destination.measurements = MeasurementsHandlerComponent.mergeMeasurements(destination.measurements, other.measurements);
        destination.estimatedMeasurements = MeasurementsHandlerComponent.mergeMeasurements(destination.estimatedMeasurements, other.estimatedMeasurements);
        return destination;
    }

    private static mergeMeasurements(destination: { [P in DataType]?: AggregatedDataPoint[] }, other: { [P in DataType]?: AggregatedDataPoint[] }): { [P in DataType]?: AggregatedDataPoint[] } {
        Object.entries(other).forEach(([dataType, otherPoints]) => {
            if (!otherPoints) return;
            let destPoints = destination[dataType];
            if (!destPoints) {
                destination[dataType] = [...otherPoints];
                return;
            }
            const pointMap = new Map<string, AggregatedDataPoint>();
            destPoints.forEach(p =>
                pointMap.set(`${p.timeRange.start}-${p.timeRange.end}`, p)
            );
            otherPoints.forEach(p => {
                const key = `${p.timeRange.start}-${p.timeRange.end}`;
                const existing = pointMap.get(key);
                if (!existing) {
                    destPoints.push(p);
                    pointMap.set(key, p);
                } else {
                    const aggregationMethod = AppContext.getAggregationMethod(dataType as DataType);
                    existing.value = aggregationMethod([existing.value, p.value]);
                }
            });
            destination[dataType] = destPoints;
        });
        return destination;
    }
}

export interface DataQueryFilters {
    timeRange: DashboardTime;
    sources: SourceInfo[];
    cleanedSources: SourceInfo[];
    allSelectedSources: SourceInfo[];
    fullScreen: boolean;
}

export interface MeasurementsHandlerOpenModalCommand {
    chartOptions?: { [P in ChartOptionType]?: ChartOptions };
    component?;
    chartModalData?: ChartModalOptions;
    container: ViewContainerRef;
    modalClasses?: string;
}

export interface MeasurementsHandlerComponentData {
    chartOptions?: { [P in ChartOptionType]?: ChartOptions };
    timeRange?: DashboardTime;
    selectedSources?: SourceInfo[];
    allSelectedSources?: SourceInfo[];
    component?;
    chartModalData?: ChartModalOptions;
}

interface MeasurementsQueryPayload {
    range?: DashboardTime;
    showIncidents?: boolean;
}